import { DateTime } from "luxon";
import { isEmptyValue } from "../helper/isEmptyValue";
import type { SyncValidationRule } from "../types/validation";

type DateValue = string | Date | DateTime | null | undefined;

function toDateTime(value: DateValue): DateTime | undefined {
  if (value instanceof DateTime) {
    return value;
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }
  if (typeof value === "string") {
    return DateTime.fromISO(value);
  }
  return undefined;
}

export function validDate<TValue extends DateValue = DateValue>(): SyncValidationRule<TValue> {
  return {
    name: "validDate",
    kind: "sync",
    dependencies: [],
    validate(value) {
      if (isEmptyValue(value)) {
        return undefined;
      }
      return toDateTime(value)?.isValid === true
        ? undefined
        : { messageKey: "validation.validDate" };
    },
  };
}

export function notFutureDate<TValue extends DateValue = DateValue>(
  now = DateTime.now(),
): SyncValidationRule<TValue> {
  return {
    name: "notFutureDate",
    kind: "sync",
    dependencies: [now.toMillis()],
    validate(value) {
      if (isEmptyValue(value)) {
        return undefined;
      }
      const date = toDateTime(value);
      return date?.isValid === true && date <= now
        ? undefined
        : { messageKey: "validation.notFutureDate" };
    },
  };
}

export function positiveDateRange<
  TValue extends readonly DateValue[] | null | undefined =
    | readonly [DateValue, DateValue]
    | null
    | undefined,
>(): SyncValidationRule<TValue> {
  return {
    name: "positiveDateRange",
    kind: "sync",
    dependencies: [],
    validate(value) {
      if (isEmptyValue(value)) {
        return undefined;
      }
      if (!Array.isArray(value) || value.length !== 2) {
        return { messageKey: "validation.positiveDateRange" };
      }
      const start = toDateTime(value[0]);
      const end = toDateTime(value[1]);
      return start?.isValid === true && end?.isValid === true && end >= start
        ? undefined
        : { messageKey: "validation.positiveDateRange" };
    },
  };
}

export function maxDateRangeMonths(
  maxMonths: number,
): SyncValidationRule<DateValue[] | null | undefined> {
  return {
    name: "maxDateRangeMonths",
    kind: "sync",
    dependencies: [maxMonths],
    validate(value) {
      if (isEmptyValue(value)) {
        return undefined;
      }
      if (!Array.isArray(value) || value.length !== 2) {
        return { messageKey: "validation.maxDateRangeMonths", params: { maxMonths } };
      }
      const start = toDateTime(value[0]);
      const end = toDateTime(value[1]);
      return start?.isValid === true &&
        end?.isValid === true &&
        end <= start.plus({ months: maxMonths })
        ? undefined
        : { messageKey: "validation.maxDateRangeMonths", params: { maxMonths } };
    },
  };
}
